import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getCurrentUser, logoutUser as serviceLogoutUser } from '../services/auth';

// Create context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUser = useCallback(async () => {
    try {
      setLoading(true);
      const user = await getCurrentUser();
      setCurrentUser(user);
      setError(null);
      console.log("AuthContext: User fetched/refreshed:", user);
      return user;
    } catch (err) {
      console.error('AuthContext: Failed to load user:', err);
      setError('Failed to authenticate user');
      setCurrentUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const loginContext = useCallback(async () => {
    console.log("AuthContext: loginContext called, attempting to fetch user.");
    return await fetchUser();
  }, [fetchUser]);
  
  const logoutContext = useCallback(async () => {
    console.log("AuthContext: logoutContext called");
    try {
      await serviceLogoutUser();
      localStorage.removeItem('token');
      localStorage.removeItem('analysisResults');
      setCurrentUser(null);
      setError(null);
      console.log("AuthContext: User logged out, currentUser set to null.");
    } catch (err) {
      console.error("AuthContext: Error during logoutContext", err);
      localStorage.removeItem('token');
      localStorage.removeItem('analysisResults');
      setCurrentUser(null);
      setError('Logout failed on server, but client state cleared.');
    }
  }, []);

  const updateUserData = useCallback((userData) => {
    console.log("AuthContext: updateUserData called with", userData);
    setCurrentUser(prevUser => ({
      ...prevUser,
      ...userData
    }));
  }, []);

  // Value object to be provided to consumers
  const value = {
    currentUser,
    loading,
    error,
    loginContext,
    logoutContext,
    updateUserData,
    fetchUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 